import React from 'react';
import {
  Box,
  Typography,
  Stack,
  LinearProgress,
  Card,
  CardContent,
  alpha,
} from '@mui/material';
import {
  Warning,
  Analytics,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getPhaseColor } from './utils';

const SummarySection = ({ latestPhase, latestResult, detectionData }) => {
  const { t } = useTranslation();
  
  const phaseInfo = getPhaseColor(latestPhase);
  const riskScore = detectionData.risk_score !== null && detectionData.risk_score !== undefined ? detectionData.risk_score : 0;
  const macroScore = detectionData.macro_score !== null && detectionData.macro_score !== undefined ? detectionData.macro_score : 0;
  const overallRisk = latestResult?.down_risk_60 !== null && latestResult?.down_risk_60 !== undefined ? latestResult.down_risk_60 * 100 : 0;

  const getPhaseGradient = (phase) => {
    if (phase === 'UP_NEUTRAL') return `linear-gradient(135deg, #16a34a 0%, #22c55e 100%)`;
    if (phase === 'EARLY_WARNING') return `linear-gradient(135deg, #ea580c 0%, #fb923c 100%)`;
    if (phase === 'CONFIRMED_DOWN') return `linear-gradient(135deg, #e11d48 0%, #f43f5e 100%)`;
    if (phase === 'SEVERE_DOWN') return `linear-gradient(135deg, #be123c 0%, #dc2626 100%)`;
    return `linear-gradient(135deg, #64748b 0%, #94a3b8 100%)`;
  };

  return (
    <Box sx={{ mb: 0 }}>
      {/* Single Compact Card */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${alpha(phaseInfo.color, 0.18)}`,
          borderRadius: 2.25,
          bgcolor: 'background.paper',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: alpha(phaseInfo.color, 0.4),
            boxShadow: `0 6px 16px ${alpha(phaseInfo.color, 0.08)}`,
          },
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, md: 1.75 } }}>
          {/* Horizontal Layout: Status Left, Metrics Right */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'auto 1fr' },
              gap: { xs: 1.75, lg: 2.25 },
              alignItems: 'center',
            }}
          >
            {/* Left: Status */}
            <Stack direction="row" alignItems="center" spacing={1.25}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 1.5,
                  background: getPhaseGradient(latestPhase),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(phaseInfo.color, 0.2)}`,
                  flexShrink: 0,
                }}
              >
                {React.cloneElement(phaseInfo.icon, { sx: { fontSize: 22, color: 'white' } })}
              </Box>
              <Box>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600, 
                    letterSpacing: 0.8, 
                    textTransform: 'uppercase', 
                    fontSize: '0.58rem', 
                    display: 'block',
                    mb: 0.15,
                  }}
                >
                  {t('downtrend.summary.currentStatus')}
                </Typography>
                <Typography 
                  fontWeight={800} 
                  sx={{ 
                    letterSpacing: -0.5, 
                    color: phaseInfo.color,
                    lineHeight: 1.1,
                    fontSize: { xs: '1rem', sm: '1.12rem', md: '1.28rem' },
                  }}
                >
                  {t(phaseInfo.labelKey)}
                </Typography>
              </Box>
            </Stack>

            {/* Right: Metrics Inline */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { 
                  xs: '1fr', 
                  sm: latestResult?.down_risk_60 !== null && latestResult?.down_risk_60 !== undefined 
                    ? 'repeat(3, 1fr)' 
                    : 'repeat(2, 1fr)' 
                },
                gap: 1.75,
              }}
            >
              {/* Overall Risk Score */}
              {latestResult?.down_risk_60 !== null && latestResult?.down_risk_60 !== undefined && (
                <Box>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      fontSize: '0.58rem', 
                      display: 'block', 
                      mb: 0.6,
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('downtrend.summary.overallRiskScore')}
                  </Typography>
                  <Typography 
                    fontWeight={800} 
                    sx={{ 
                      fontFamily: 'monospace', 
                      mb: 0.6, 
                      color: phaseInfo.color,
                      lineHeight: 1,
                      fontSize: { xs: '1rem', sm: '1.1rem', md: '1.22rem' },
                    }}
                  >
                    {overallRisk.toFixed(1)}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={overallRisk}
                    sx={{
                      height: 4,
                      borderRadius: 999,
                      bgcolor: alpha(phaseInfo.color, 0.1),
                      '& .MuiLinearProgress-bar': {
                        bgcolor: phaseInfo.color,
                        borderRadius: 999,
                      },
                    }}
                  />
                </Box>
              )}

              {/* Risk Score */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.45} sx={{ mb: 0.6 }}>
                  <Warning sx={{ fontSize: 14, color: phaseInfo.color, opacity: 0.5 }} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      fontSize: '0.58rem',
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('downtrend.summary.riskScore')}
                  </Typography>
                </Stack>
                <Typography 
                  fontWeight={800} 
                  sx={{ 
                    fontFamily: 'monospace', 
                    color: phaseInfo.color,
                    lineHeight: 1,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.22rem' },
                  }}
                >
                  {riskScore !== null && riskScore !== undefined ? `${riskScore.toFixed(1)}%` : t('downtrend.riskLabels.na')}
                </Typography>
              </Box>

              {/* Macro Score */}
              <Box>
                <Stack direction="row" alignItems="center" spacing={0.45} sx={{ mb: 0.6 }}>
                  <Analytics sx={{ fontSize: 14, color: phaseInfo.color, opacity: 0.5 }} />
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary', 
                      fontWeight: 600, 
                      textTransform: 'uppercase', 
                      fontSize: '0.58rem',
                      letterSpacing: 0.5,
                    }}
                  >
                    {t('downtrend.summary.macroScore')}
                  </Typography>
                </Stack>
                <Typography 
                  fontWeight={800} 
                  sx={{ 
                    fontFamily: 'monospace', 
                    color: phaseInfo.color,
                    lineHeight: 1,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.22rem' },
                  }}
                >
                  {macroScore !== null && macroScore !== undefined ? `${macroScore.toFixed(1)}%` : t('downtrend.riskLabels.na')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SummarySection;

